import { useEffect } from 'react';
import { create } from 'zustand';

interface ISignWellEmbed {
  url: string;
  events: {
    completed: (e: { id: string; url: string }) => void;
    closed: (e: { id: string }) => void;
    iframeLoaded?: (e: { id: string }) => void;
    documentLoaded?: (e: { id: string }) => void;
    error?: () => void;
  };
  open: () => void;
  close: () => void;
}

interface SignWellScriptState {
  isLoaded: boolean;
  isLoading: boolean;
  error: string | null;
  isContractOpen: boolean;
  signWellEmbed: ISignWellEmbed | null;
  loadScript: () => void;
  setContractOpen: (isOpen: boolean) => void;
  setSignWellEmbed: (embed: ISignWellEmbed | null) => void;
}

export const useSignWellScriptStore = create<SignWellScriptState>((set, get) => ({
  isLoaded: false,
  isLoading: false,
  error: null,
  isContractOpen: false,
  signWellEmbed: null,
  setContractOpen: isOpen => set({ isContractOpen: isOpen }),
  setSignWellEmbed: embed => set({ signWellEmbed: embed }),
  loadScript: () => {
    if (get().isLoaded || get().isLoading) return;
    set({ isLoading: true, error: null });

    const existingScript = document.querySelector('script[src*="signwell.com/assets/embedded.js"]');
    if (existingScript) {
      set({ isLoaded: true, isLoading: false });
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://static.signwell.com/assets/embedded.js';
    script.type = 'text/javascript';
    script.async = true;

    script.onload = () => set({ isLoaded: true, isLoading: false });
    script.onerror = () =>
      set({ isLoaded: false, isLoading: false, error: 'Error loading script' });

    document.body.appendChild(script);
  }
}));

export const useSignWellScript = () => {
  const {
    isLoaded,
    isLoading,
    error,
    loadScript,
    isContractOpen,
    setContractOpen,
    signWellEmbed,
    setSignWellEmbed
  } = useSignWellScriptStore();

  useEffect(() => {
    loadScript();
  }, [loadScript]);

  const openSignWell = (
    signUrl: string,
    options?: { onComplete?: () => void; onClose?: () => void }
  ) => {
    if (!isLoaded || !window.SignWellEmbed || isContractOpen) return;

    const container = document.getElementById('contract-wrapper');
    if (container && container.querySelector('iframe')) {
      return;
    }

    const embed = new window.SignWellEmbed({
      url: signUrl,
      containerId: 'contract-wrapper',
      events: {
        completed: _e => {
          options?.onComplete?.();
        },
        closed: _e => {
          options?.onClose?.();
        }
      },
      allowClose: false,
      allowDecline: false
    });

    setSignWellEmbed(embed);
    setContractOpen(true);
    embed.open();
  };

  const closeSignWell = () => {
    if (!isContractOpen) return;
    try {
      signWellEmbed?.close();
    } catch {
      // Ignorar errores si el iframe ya fue destruido
    }
    setSignWellEmbed(null);
    setContractOpen(false);
  };

  const resetSignWell = () => {
    try {
      signWellEmbed?.close();
    } catch (err) {
      console.error('Error closing sign well', err);
    }

    const container = document.getElementById('contract-wrapper');
    if (container) {
      container.innerHTML = '';
    }

    setSignWellEmbed(null);
    setContractOpen(false);
  };

  return {
    isScriptLoaded: isLoaded,
    isScriptLoading: isLoading,
    scriptError: error,
    isContractOpen,
    openSignWell,
    closeSignWell,
    resetSignWell
  };
};
